// =============================================================================
// Modal (UX-audit §5.3 / K-forms 18-point standard) — the one modal shell.
//
// 187 local `*Modal` components hand-copy a `fixed inset-0` backdrop + card,
// which is why every keyboard, focus, aria, escape and dirty-guard finding is
// ~130 files wide instead of one. This shell owns the structural half of the
// K-forms contract so a page-level modal only supplies fields + a mutation:
//
//   1. Root is a <form onSubmit> when `onSubmit` is given — Enter submits from
//      any field; the primary button is type="submit".
//   2. role="dialog" + aria-modal + aria-labelledby, Tab trapped inside the
//      card, focus restored to the trigger on close. Escape belongs to the
//      TOP modal only, and stands down entirely while an imperative dialog
//      (confirmDialog/alertDialog) is up.
//   3. First editable field IN THE BODY focused on mount (honours an explicit
//      autoFocus) — never the header's own Close button. On TOUCH the dialog
//      itself takes focus instead, so opening one never raises the keyboard
//      over what the person opened it to read (`initialFocus`, W-12).
//   4. EXACTLY ONE closer: attemptClose (useDirtyClose) wired to backdrop, X,
//      Cancel and Escape. No code path calls onClose directly — pass a
//      `snapshot` string built synchronously from every field and a stray
//      backdrop tap can never discard typed work (W4.2 §4.2).
//   5. Dismissing never writes. Two real actions are two buttons
//      (choiceDialog), never OK-vs-Cancel.
//   6. The submit button is disabled while `isPending` and reads `submitPendingLabel` —
//      double-submit is structurally impossible. Hand the shell your
//      mutation's flags via `overlay` and it renders <SubmitOverlay/> too.
//   7. Heights are dvh so the footer row stays above the iOS toolbar, and the
//      backdrop fits the VISIBLE viewport (`--kb-inset` / `--vv-top` from
//      useVisualViewportInset) so Save stays above an on-screen keyboard.
//   8. The page behind does not scroll while any Modal is open (a ref-counted
//      lock on <html>), and the body's own scroll never chains to it.
//   9. Jump-to-required: a Save with a `required` control left EMPTY does not
//      call onSubmit. The shell scrolls the first one to the middle of the
//      body, focuses it, rings every empty one red (aria-invalid) and each
//      <Field> holding one prints "Required" under it; a polite live region
//      says "<caption> is required." A mark clears the moment its control
//      gets a value. So: mark controls `required` (Field's `required` for the
//      asterisk + the control's own attribute) and DON'T add `submitDisabled`
//      for emptiness — a greyed Save never said why, and on a phone the empty
//      field was usually scrolled out of sight (Thomas, 3 Oct). Emptiness
//      only — never step/min/max/pattern (see `noValidate` below). Custom
//      pickers opt in through `requiredWidgetProps` (src/lib/requiredFields).
//
// Mount = open (the app's idiom is `{open && <MyModal/>}`); there is no
// `open` prop. View-only modals omit `onSubmit` and get a <div> root.
//
// ⚠ Passing `onSubmit` makes the card a real <form>, and a bare <button> in a
// form defaults to type="submit". Every non-submitting button inside a
// submitting Modal therefore needs an explicit type="button" — otherwise a
// "Select all" or a chip toggle silently posts the form. Converting a modal
// means auditing its buttons — AND the components it renders, which is where
// the worker editor's enrolment panel hid (#1905). scripts/check-form-buttons
// fails the build on a bare button anywhere inside a submitting Modal, and
// handleSubmit below ignores a submit whose button never said type="submit",
// so a miss costs a dead tap in DEV (with a console warning) rather than a
// stray save. Neither replaces the audit: a bare button still does nothing.
// The mutation half of the contract (onError everywhere, friendlyError,
// useSaveDb + withTimeout) stays at the call site — see K-forms.md.
// =============================================================================

import { useCallback, useEffect, useId, useMemo, useRef, useState, type ComponentProps, type CSSProperties, type FormEvent, type ReactNode, type RefObject } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useDirtyClose } from '@/hooks/useDirtyClose'
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset'
import { imperativeDialogOpen } from '@/components/Dialogs'
import SubmitOverlay from '@/components/SubmitOverlay'
import { coarsePointer, openingFocus, trapTab, type InitialFocus } from '@/lib/modalFocus'
import { lockPageScroll } from '@/lib/pageScrollLock'
import { exitAnimates, playExit, type ExitFrom } from '@/lib/motion'
import { useSheetDrag } from '@/hooks/useSheetDrag'
import {
  RequiredFieldsContext, findMissingRequired, markMissing, missingAnnouncement, stillMissing, unmarkMissing,
} from '@/lib/requiredFields'

// Mounted-modal stack. Escape belongs to the TOP modal only — a drawer that
// opens a second Modal (Help → Report a bug) must not close both at once.
const modalStack: string[] = []

// Surface palettes, same idiom (and prop name) as QueryStateGate's: 'dark' is
// the app shell every modal was written against, 'light' is for a dialog that
// opens over a WHITE page — a printed sheet (the e-signature pad over a BCEA
// contract) or a light-themed page (SIZA). Rendering the dark card there puts
// a near-black box on paper, which is why those two stayed hand-rolled with a
// recorded exemption instead of being converted; this is that follow-up.
//
// ⚠ The dark values are byte-identical to the classes that were hardcoded
// here, so every existing consumer renders exactly as before. `btn-ghost` is
// surface-raised (a dark button) — hence the light palette carries its own cancel
// styling rather than changing a global class 44 components share.
const SURFACE = {
  dark: {
    card:      'surface-sunk hairline-strong',
    edge:      'hairline',
    title:     'text-white',
    subtitle:  'text-muted',
    closeBtn:  'text-gray-400 hover:text-white hover:bg-gray-800',
    cancelBtn: 'btn-ghost',
  },
  light: {
    card:      'bg-white border-gray-300',
    edge:      'border-gray-200',
    title:     'text-gray-900',
    subtitle:  'text-gray-600',
    closeBtn:  'text-gray-500 hover:text-gray-900 hover:bg-gray-100',
    cancelBtn: 'btn bg-gray-200 hover:bg-gray-300 text-gray-900',
  },
} as const

// Local: nothing outside needs to name it yet (callers pass the literal),
// and an unused export is dead code the audit gate rightly flags.
type ModalSurface = keyof typeof SURFACE

const SIZE_CLASS = {
  sm:  'sm:max-w-sm',
  md:  'sm:max-w-md',
  lg:  'sm:max-w-lg',
  xl:  'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  // A multi-field CAPTURE form (farm audit X-09, W-13): the full-width sheet on
  // a phone like every size, ~720px on a tablet — where `md` left a 448px box
  // in the middle of a 1024px screen and 2.5 screens of scrolling inside it.
  // Pair it with `cq-*:grid-cols-*` inside the body, never `sm:`.
  form: 'sm:max-w-[45rem]',
  // A locked-plan snapshot pivots one column per ISO week, so its viewer was
  // hand-rolled at max-w-6xl. Converting it to 4xl would have narrowed a
  // read-only table by a third to fit the shell — the token is the cheaper
  // half of that trade.
  '6xl': 'sm:max-w-6xl',
} as const

// The backdrop covers the VISIBLE viewport, not the layout one: with an
// on-screen keyboard up on iOS (which ignores `interactive-widget`), the layout
// keeps its full height and the keys cover its bottom, so a bottom sheet's
// footer — Save — sat under them (farm audit X-01). The variables come from
// useVisualViewportInset and are 0 whenever nothing is covering the page, so
// this is exactly `inset: 0` then.
const BACKDROP_STYLE: CSSProperties = { top: 'var(--vv-top, 0px)', bottom: 'var(--kb-inset, 0px)' }

// 90dvh as before, but never taller than the visible area above a keyboard
// (less a sliver of backdrop to tap). With no keyboard the second term is the
// larger, so the cap is 90dvh exactly as it was.
//
// A CLASS, not an inline style: the floor's Chrome 86 handsets predate dvh
// (Chrome 108), and an inline style never reaches PostCSS. This value holds
// var(), so an old engine ACCEPTED it inline (var() defers the check) and then
// computed it to `none` — the card had no height cap, and a tall form ran off
// the top and bottom of the screen with Save out of reach (measured on a
// pre-dvh Chromium 92: the style attribute kept, max-height `none`).
// As a class, PostCSS rewrites the rule to `min(90vh, calc(100vh - …))` IN
// PLACE and puts its dvh form in an `@supports (height: 1dvh)` copy right
// after it (scripts/lib/viewportUnitFallback.mjs, shape 2) — keyboard term
// kept in both. NOT a vh twin in front: with var() in the value a twin is
// inert, because the engine only finds the unknown unit after it has
// discarded the twin.
const CARD_MAX_H = 'max-h-[min(90dvh,calc(100dvh-var(--kb-inset,0px)-var(--vv-top,0px)-0.5rem))]'

// Phone sheets sit flush to the viewport bottom, so the footer's buttons
// would land under the home indicator without the bottom padding. env()
// resolves to 0 everywhere else, and the centred sm+ card is unaffected; with
// a keyboard up the keys cover the indicator, so the padding stands down.
const CARD_STYLE: CSSProperties = {
  paddingBottom: 'max(0px, calc(env(safe-area-inset-bottom) - var(--kb-inset, 0px)))',
}

// Exactly what <SubmitOverlay/> takes — derived, never re-typed. This was a
// hand-copied subset, and the copy fell behind: it omitted `pendingLabel` and
// the watchdog copy, so a modal that is not a save (the email review screen)
// could not say "Sending…" without a type error, and shipped saying "Saved"
// for a document that had left the building.
type OverlayFlags = ComponentProps<typeof SubmitOverlay>

interface ModalProps {
  /** Title text (or a custom node) — becomes the dialog's aria-labelledby. */
  title: ReactNode
  /** Second header line: the record this dialog is about (item, pallet, worker).
   *  Deliberately outside the accessible name — see the header comment. */
  subtitle?: ReactNode
  /** The ONLY close callback. The shell wires it through the dirty guard. */
  onClose: () => void
  /** Synchronous string of every field's value, seeded at mount — the dirty
   *  baseline. Omit for view-only modals (never dirty). */
  snapshot?: string
  /** Form submit handler. Present → the card is a <form> and Enter submits.
   *  The shell calls preventDefault() before invoking it. */
  onSubmit?: () => void
  /** Disables submit + shows `submitPendingLabel`. Wire your mutation's isPending. */
  isPending?: boolean
  /** Greys the submit button out. ⚠️ NOT for "a required field is empty" —
   *  mark that control `required` and the shell jumps to it on Save, says
   *  "Required" and refuses to submit (contract item 9). A greyed Save never
   *  told anyone WHY. Keep this for a state no field can fix: nothing chosen
   *  from a list, a refusal the page computed, a record that is not loaded. */
  submitDisabled?: boolean
  submitLabel?: string
  /** What the button reads while pending. Defaults to "Saving…"; give it the
   *  real verb on a modal that does not save ("Sending…"). */
  submitPendingLabel?: string
  cancelLabel?: string
  /** Extra content on the footer's left edge (e.g. a delete button). */
  footerStart?: ReactNode
  /** Replace the default Cancel/submit footer entirely. Your buttons must
   *  close via the `attemptClose` render-prop, never onClose. */
  footer?: (attemptClose: () => void) => ReactNode
  /** No footer at all (list/pick modals whose rows are the actions). */
  noFooter?: boolean
  /** Remove EVERY dismissal route — no X, no backdrop tap, no Escape. The
   *  footer's own button is then the only way out.
   *
   *  Reserved for a one-time reveal: a pairing secret shown once and not
   *  retrievable. Losing it is not an inconvenience — operators who lost a
   *  kiosk code went and rotated a LIVE kiosk to mint a new one, which
   *  orphaned it (Anchor 07-29 and 08-05). A stray backdrop tap must not be
   *  able to cause that.
   *
   *  Never use this to make an ordinary form harder to abandon. Dismissing a
   *  capture form writes nothing; trapping someone in one is the opposite of
   *  the dirty guard, which exists so they can leave SAFELY. If there is any
   *  way to get the content back, this is the wrong prop. */
  undismissable?: boolean
  /** Hide the whole dialog from print. For pages that print themselves (an
   *  asset tag, a report) — an open modal would otherwise paper over the
   *  printed sheet. Matches PageHeader's `printHidden`. */
  printHidden?: boolean
  /** Card width at sm+ (full-width sheet on phones regardless). */
  size?: keyof typeof SIZE_CLASS
  /** 'light' for a dialog opening over a WHITE page — a printed sheet or a
   *  light-themed page. Defaults to the dark app shell. */
  surface?: ModalSurface
  /** Mutation flags → the shell renders <SubmitOverlay/> for you. */
  overlay?: OverlayFlags
  /** Where focus lands on open (src/lib/modalFocus.ts). 'auto' (default): the
   *  first field with a mouse, the dialog itself on touch. 'field': the first
   *  field everywhere — a dialog whose whole job is one number or one scan.
   *  'card': the dialog everywhere — a read-only sheet. */
  initialFocus?: InitialFocus
  children: ReactNode
}

// fallow-ignore-next-line complexity
// How long a dismissed Modal waits, still mounted, before deciding its parent
// refused the close and bringing the card back.
const REFUSED_CLOSE_MS = 250

// Dismiss exit (src/lib/motion.ts playExit). It runs only AFTER the dirty
// guard has said yes, and only through this closer: a close the parent
// performs (after a save, via refetchAndClose) never gets here and stays
// instant. `exiting` also blocks a second tap landing on the fading card.
// Its own hook so Modal's body stays inside fallow's complexity budget.
function useDismissExit(
  onClose: () => void, cardRef: RefObject<HTMLElement | null>,
  fromRef: RefObject<ExitFrom | undefined>, restoreRef: RefObject<(() => void) | undefined>,
) {
  const backdropRef = useRef<HTMLDivElement | null>(null)
  const exitingRef = useRef(false)
  const [exiting, setExiting] = useState(false)
  const mountedRef = useRef(true)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false } }, [])
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const closeWithExit = () => {
    if (exitingRef.current) return
    // Nothing to animate (reduced motion, jsdom, an old engine): close at once,
    // as before the exit existed — no closing state, no refused-close timer.
    if (!exitAnimates(backdropRef.current, cardRef.current)) { onCloseRef.current(); return }
    exitingRef.current = true
    setExiting(true)
    void playExit(backdropRef.current, cardRef.current, fromRef.current ?? undefined).then(() => {
      // The parent may have unmounted us mid-exit; closing then is moot.
      if (!mountedRef.current) return
      onCloseRef.current()
      // A parent may REFUSE the close (a busy save ignores its guarded
      // onClose). Then the card must come back: if we are still mounted after
      // REFUSED_CLOSE_MS, drop the held exit and re-arm. Not the next task: a
      // parent whose onClose awaits something before closing would see the
      // card flash back for a frame and then vanish.
      setTimeout(() => {
        if (!mountedRef.current) return
        for (const el of [backdropRef.current, cardRef.current]) el?.getAnimations?.().forEach(a => a.cancel())
        exitingRef.current = false
        // A dragged sheet left inline styles at its release offset: drop them.
        restoreRef.current?.()
        setExiting(false)
      }, REFUSED_CLOSE_MS)
    })
  }
  // The backdrop's class suffix while exiting: no second tap lands on it.
  return { backdropRef, exitingClass: exiting ? ' pointer-events-none' : '', exitingRef, exiting, closeWithExit }
}

// The shell every capture form uses. On main it sat just under fallow's
// cognitive/CRAP line; the one useDismissExit call tipped it over, and the
// exit logic itself already lives in that hook. Unit-covered in Modal.test.tsx.
// fallow-ignore-next-line complexity
export default function Modal({
  title, subtitle, onClose, snapshot = '', onSubmit, isPending = false, submitDisabled = false,
  submitLabel = 'Save', submitPendingLabel = 'Saving…', cancelLabel = 'Cancel', footerStart,
  footer, noFooter = false, printHidden = false, size = 'lg', overlay,
  undismissable = false, surface = 'dark', initialFocus = 'auto', children,
}: ModalProps) {
  const s = SURFACE[surface]
  const cardRef = useRef<HTMLDivElement | HTMLFormElement | null>(null)
  const dragFrom = useRef<ExitFrom | undefined>(undefined)
  const restoreDrag = useRef<(() => void) | undefined>(undefined)
  const { backdropRef, exitingClass, exitingRef, exiting, closeWithExit } = useDismissExit(onClose, cardRef, dragFrom, restoreDrag)
  const attemptClose = useDirtyClose(snapshot, closeWithExit)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const titleId = useId()
  const instanceId = useId()
  const [trigger] = useState<HTMLElement | null>(() => document.activeElement as HTMLElement | null)
  // Read once: the opening focus is decided at mount, never re-run mid-edit.
  const focusMode = useRef(initialFocus)
  useVisualViewportInset()

  // The page behind stays put while this (or any) Modal is open.
  useEffect(() => lockPageScroll(), [])

  // Push/pop this modal on the stack so Escape can find the top one.
  useEffect(() => {
    modalStack.push(instanceId)
    return () => {
      const i = modalStack.lastIndexOf(instanceId)
      if (i !== -1) modalStack.splice(i, 1)
    }
  }, [instanceId])

  // Re-entry guard: Escape or a second backdrop tap while the discard-confirm
  // is already up must not stack a second confirm on top of it.
  const closing = useRef(false)
  const guardedClose = useCallback(async () => {
    if (closing.current) return
    closing.current = true
    try { await attemptClose() } finally { closing.current = false }
  }, [attemptClose])

  // Drag a phone sheet down to close it (src/hooks/useSheetDrag.ts). The
  // release goes through the SAME closer as the X, so a dirty form still asks.
  // If the guard is up (confirm showing) or says no, the sheet springs back;
  // if it says yes, closeWithExit's exit continues from the release offset.
  const busy = isPending || !!(overlay && (overlay.isPending || overlay.isSuccess || overlay.isError))
  const drag = useSheetDrag({
    cardRef, backdropRef, bodyRef, enabled: !undismissable && !busy && !exiting,
    onDismiss: (y, backdropOpacity) => {
      dragFrom.current = { y, backdropOpacity }
      const done = guardedClose()
      if (imperativeDialogOpen()) drag.springBack()
      void done.finally(() => {
        if (!exitingRef.current) { dragFrom.current = undefined; drag.springBack() }
      })
    },
  })
  restoreDrag.current = drag.clear

  // Focus: land on the first editable field, restore to the opener on close.
  // Scanners and keyboards must land somewhere real (K-forms #3) — but on a
  // TOUCH device the dialog itself takes focus, because a focused field there
  // raises the on-screen keyboard over the dialog the person opened to read
  // (farm audit X-12). The rules live in openingFocus (src/lib/modalFocus.ts).
  //
  // Search the BODY, not the whole card, and try real fields before buttons:
  // a selector list returns the first match in DOCUMENT order, so querying the
  // card for "input, …, button" hands focus to the header's own X — every
  // modal would open with Close focused and Enter would dismiss it.
  //
  // React applies `autoFocus` during commit and never emits the attribute,
  // so an [autofocus] query finds nothing — but focus has ALREADY landed by
  // the time this effect runs. If it is inside the card, the page picked a
  // field on purpose and openingFocus leaves it alone (bar a numeric field on
  // touch, which would raise the keypad on open).
  //
  // Decided ONCE and remembered: under StrictMode (dev) React re-runs this
  // effect after a simulated unmount whose cleanup below has already handed
  // focus back to the opener — a second decision would see the opener, not
  // the autoFocused field, and the dev build would disagree with production.
  const openedOn = useRef<Element | null | undefined>(undefined)
  useEffect(() => {
    if (openedOn.current === undefined) {
      openedOn.current = openingFocus({
        body: bodyRef.current, card: cardRef.current, active: document.activeElement,
        coarse: coarsePointer(), mode: focusMode.current,
      }) ?? document.activeElement
    }
    const target = openedOn.current as HTMLElement | null
    target?.focus?.()
  }, [])

  // Restore focus to whatever opened the modal. `trigger` is captured during
  // the FIRST RENDER, before commit — by the time an effect runs, an
  // autoFocused field has already taken activeElement and the opener is lost.
  useEffect(() => () => { trigger?.focus?.() }, [trigger])

  // Escape closes (through the dirty guard) and Tab stays inside the card.
  //
  // Two standdowns, both learned from review: an imperative dialog
  // (confirmDialog/alertDialog) listens on WINDOW while this listens on
  // DOCUMENT, and document-phase runs first — so while one is up we must not
  // consume the key, or we would close ourselves and orphan the confirm we
  // raised. And only the TOP modal in the stack owns Escape.
  // Escape is ours only when no imperative dialog is up AND we are the top of
  // the stack — otherwise we would close ourselves and orphan the confirm we
  // just raised.
  const ownsEscape = useCallback(
    () => !undismissable && !imperativeDialogOpen() && modalStack[modalStack.length - 1] === instanceId,
    [instanceId, undismissable],
  )

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!ownsEscape()) return
        e.stopPropagation()
        void guardedClose()
        return
      }
      if (e.key !== 'Tab') return
      const card = cardRef.current
      if (card) trapTab(e, card, document.activeElement)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [guardedClose, ownsEscape])

  // Jump-to-required (contract item 9). `flagged` is what the last refused
  // Save marked; the ref mirrors it so `recheck` reads the live list without
  // re-subscribing the listeners below on every change.
  const [flagged, setFlagged] = useState<readonly Element[]>([])
  const flaggedRef = useRef<readonly Element[]>([])
  const [announcement, setAnnouncement] = useState('')
  const refusals = useRef(0)

  const flag = useCallback((next: readonly Element[]) => {
    for (const el of flaggedRef.current) if (!next.includes(el)) unmarkMissing(el)
    for (const el of next) markMissing(el)
    flaggedRef.current = next
    setFlagged(next)
  }, [])

  // A mark lasts until its control has a value, and no longer. Three things
  // can give it one, so three things re-check: typing (the form's onChange —
  // React's bubbles from every field), a parent's state change (a picker's
  // pick, a programmatic fill — any of which re-renders this shell, hence the
  // no-deps effect), and a custom control flipping its empty flag from state
  // that lives inside the body (the MutationObserver). Each is a no-op while
  // nothing is flagged, which is nearly always.
  const recheck = useCallback(() => {
    const prev = flaggedRef.current
    if (prev.length === 0) return
    const still = prev.filter(stillMissing)
    if (still.length === prev.length) return
    flag(still)
    if (still.length === 0) setAnnouncement('')
  }, [flag])

  useEffect(() => { recheck() })

  useEffect(() => {
    const card = cardRef.current
    if (!card || flagged.length === 0 || typeof MutationObserver === 'undefined') return
    const mo = new MutationObserver(recheck)
    mo.observe(card, {
      subtree: true, attributes: true,
      attributeFilter: ['data-required-empty', 'disabled', 'readonly', 'required', 'hidden'],
    })
    return () => mo.disconnect()
  }, [flagged, recheck])

  /** True when Save must not go through: every empty required control is
   *  flagged, the first is scrolled to the middle of the body and focused —
   *  inside the tap's own handler, so iOS raises the keyboard for it. */
  function refuseIfMissing(form: HTMLFormElement): boolean {
    const missing = findMissingRequired(form)
    if (missing.length === 0) return false
    flag(missing)
    refusals.current += 1
    // Most screen readers skip a live region whose text did not change, so a
    // second refused Save would say nothing. A trailing no-break space on
    // alternate refusals makes the same sentence a change.
    setAnnouncement(missingAnnouncement(missing) + (refusals.current % 2 === 0 ? '\u00A0' : ''))
    const first = missing[0] as HTMLElement
    first.scrollIntoView?.({ block: 'center' })
    first.focus?.({ preventScroll: true })
    return true
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (isPending) return
    // Only a button that DECLARED itself a submit button may submit. In a form,
    // a bare <button> is type="submit" by default — so a panel's "Enrol face" or
    // a chip toggle rendered inside the card submitted the whole modal on every
    // tap (the worker editor saved the profile and closed itself under the
    // enrolment wizard, #1905). check-form-buttons keeps CONVERTED files clean;
    // this covers the 187 modals still to convert, each conversion being exactly
    // this hazard. The shell's own primary and any footer button that means it
    // carry type="submit" explicitly. Enter-key submission arrives with the
    // form's default button (that same explicit one) or with no submitter at
    // all, so both still submit.
    const submitter = (e.nativeEvent as SubmitEvent).submitter
    if (submitter && submitter.getAttribute('type') !== 'submit') {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[Modal] ignored a submit from a button with no type="submit" — give it type="button":', submitter)
      }
      return
    }
    if (e.currentTarget instanceof HTMLFormElement && refuseIfMissing(e.currentTarget)) return
    onSubmit?.()
  }

  const header = (
    <div {...drag.headerProps} className={`relative px-4 py-3 border-b ${s.edge} flex items-center gap-3 shrink-0`}>
      {/* iOS-style grabber: phone sheet only. The whole header is the handle. */}
      <span aria-hidden="true" className={`sm:hidden absolute top-1 left-1/2 -translate-x-1/2 h-[5px] w-9 rounded-full bg-current opacity-40 pointer-events-none ${s.subtitle}`} />
      {/* Title and its optional subtitle stack in ONE flex child so the close
          button stays put and both lines truncate rather than pushing it off.
          The subtitle is the record the dialog is about — the item, the pallet,
          the worker — which several dialogs previously had to repeat in their
          body because this shell could only carry one line. It is NOT part of
          the accessible name: aria-labelledby stays on the h2, so a screen
          reader announces "Edit stock item", not the title and the item run
          together. */}
      <div className="flex-1 min-w-0">
        <h2 id={titleId} className={`truncate text-base font-bold ${s.title}`}>{title}</h2>
        {subtitle && <p className={`truncate text-xs ${s.subtitle}`}>{subtitle}</p>}
      </div>
      {/* 44px target — the app's HIG floor (see BackButton); a p-2 icon button
          measures 34px and several hand-rolled modals had deliberately padded
          theirs to 44 before this shell existed. Absent entirely when
          `undismissable`: an X that did nothing would be worse than none. */}
      {!undismissable && (
        <button type="button" onClick={() => void guardedClose()} aria-label="Close"
          className={`h-11 w-11 -mr-2 shrink-0 flex items-center justify-center rounded-lg ${s.closeBtn}`}>
          <X size={18} />
        </button>
      )}
    </div>
  )

  const defaultFooter = (
    <div className={`px-4 py-3 border-t ${s.edge} flex items-center gap-2 shrink-0`}>
      {footerStart}
      <div className="flex-1" />
      <button type="button" onClick={() => void guardedClose()} className={s.cancelBtn}>{cancelLabel}</button>
      {onSubmit && (
        <button type="submit" disabled={isPending || submitDisabled} className="btn-primary disabled:opacity-50 flex items-center gap-2">
          {isPending && <Loader2 size={14} className="animate-spin" />}
          {isPending ? submitPendingLabel : submitLabel}
        </button>
      )}
    </div>
  )

  // Keyed on WHETHER there is an onSubmit, not on the callback: pages pass a
  // fresh arrow every render, and a new context object each time would
  // re-render every Field in the body for nothing.
  const submitting = !!onSubmit
  const required = useMemo(
    () => (submitting ? { missing: flagged, surface } : null),
    [submitting, flagged, surface],
  )

  const body = (
    <RequiredFieldsContext.Provider value={required}>
      {header}
      {/* The body is the size container for `cq-*:` variants (W-13): a grid
          in here splits on the dialog's width, not the screen's. inline-size
          containment only — a fixed picker sheet inside still covers the
          screen (it applies no layout containment). */}
      <div ref={bodyRef} {...drag.bodyProps} className="overflow-y-auto overscroll-contain flex-1 min-h-0 p-4 [container-type:inline-size]">{children}</div>
      {!noFooter && (footer ? footer(() => void guardedClose()) : defaultFooter)}
      {overlay && <SubmitOverlay {...overlay} />}
      {/* The spoken half of jump-to-required. ONE region for the whole form
          rather than an aria-describedby per control: the shell would have
          to splice ids onto controls it does not render (merging any the
          page set), and the visible "Required" lives inside each Field's
          <label>, where it would become part of the control's NAME — so it
          is aria-hidden there and said here instead. aria-live, not
          role="status": a page's own status region must stay the only one
          getByRole('status') finds. */}
      {onSubmit && <div aria-live="polite" aria-atomic="true" className="sr-only">{announcement}</div>}
    </RequiredFieldsContext.Provider>
  )

  // Opening motion (tailwind.config.ts `sheet-in` / `dialog-in`): the card
  // slides up as a phone sheet and fades as a centred dialog; the backdrop
  // only fades. `motion-safe:` keeps both off under reduced motion.
  const cardClass = `${s.card} border-t sm:border rounded-t-2xl sm:rounded-2xl w-full ${SIZE_CLASS[size]} ${CARD_MAX_H} flex flex-col overflow-hidden outline-none motion-safe:animate-sheet-in motion-safe:sm:animate-dialog-in`
  const cardStyle = CARD_STYLE

  return (
    // Same as Dialogs: the backdrop is a convenience. Escape belongs to the TOP
    // modal via `modalStack` (see the header), and `undismissable` removes
    // every route including this one. Keyboard users are served by the focus
    // trap and Escape, neither of which this rule can see.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div ref={backdropRef} data-theme={surface === 'dark' ? 'dark' : undefined}
      className={`fixed inset-x-0 z-50 bg-black/60 flex items-end sm:items-center justify-center sm:p-4 motion-safe:animate-backdrop-in${printHidden ? ' print:hidden' : ''}${exitingClass}`}
      style={BACKDROP_STYLE}
      // mousedown, not click: a text-selection drag that ends on the backdrop
      // must not read as a dismissal tap.
      onMouseDown={e => { if (!undismissable && e.target === e.currentTarget) void guardedClose() }}>
      {onSubmit ? (
        // noValidate: the card only became a <form> when this shell landed, so
        // browser constraint validation (step/min/max on number inputs that
        // were never inside a form) would silently refuse to submit values the
        // page itself considers valid. Validation stays the app's own job —
        // bar EMPTINESS, which the shell checks itself (item 9) and nothing
        // else: validity.valueMissing still answers under noValidate.
        <form ref={cardRef as RefObject<HTMLFormElement>} onSubmit={handleSubmit} onChange={recheck} noValidate
          tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}
          className={cardClass} style={cardStyle}>
          {body}
        </form>
      ) : (
        <div ref={cardRef as RefObject<HTMLDivElement>}
          tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}
          className={cardClass} style={cardStyle}>
          {body}
        </div>
      )}
    </div>
  )
}
